import { getAccountData } from "../../ts/commonTools";
import { resolveMystudioApiOrigin } from "../../config/localPreview";
import { isPublicPasswordlessDemoSession } from "./previewGuard";
import {
    ApplyMystudioReviewEventInput,
    CreateMystudioInquiryInput,
    CreateProductDraftInput,
    MystudioApiEnvelope,
    MystudioApiMeta,
    MystudioErrorKind,
    MystudioLoginResult,
    SubmitThemeSelectionInput,
    UpdateMystudioNotificationPreferencesInput,
    UpdateMystudioShippingInput,
} from "./types";
import {
    V4AvailabilityDto,
    V4ActionItemDto,
    V4ContentListDto,
    V4HomeDto,
    V4GalleryPostDto,
    V4GallerySectionDto,
    V4InquiryDto,
    V4MeDto,
    V4NotificationDto,
    V4NotificationPreferencesDto,
    V4PageResultDto,
    V4PhotoAssetDto,
    V4PhotoDeliveryDto,
    V4ProductDto,
    V4ScheduleDto,
    V4ThemeDto,
    V4TermsDto,
    V4ReviewEventDto,
} from "./dto";

export const MYSTUDIO_AUTH_REQUIRED_EVENT = "mystudio:authentication-required";
export const DEFAULT_V4_TIMEOUT_MS = 15_000;

export class MystudioApiError extends Error {
    readonly kind: MystudioErrorKind;
    readonly status: number | null;
    readonly requestId: string;
    readonly details: unknown;

    constructor({
        message,
        kind,
        status = null,
        requestId = "",
        details = null,
    }: {
        message: string;
        kind: MystudioErrorKind;
        status?: number | null;
        requestId?: string;
        details?: unknown;
    }) {
        super(message);
        this.name = "MystudioApiError";
        this.kind = kind;
        this.status = status;
        this.requestId = requestId;
        this.details = details;
    }
}

function normalizeOrigin(origin = resolveMystudioApiOrigin()): string {
    return String(origin || "").trim().replace(/\/+$/, "");
}

export function getMystudioV4Origin(): string {
    return normalizeOrigin();
}

export function isMystudioV4Configured(): boolean {
    return Boolean(getMystudioV4Origin());
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
    const origin = getMystudioV4Origin();
    if (!origin) {
        throw new MystudioApiError({
            message: "MyStudio API 연결 주소가 설정되지 않았습니다.",
            kind: "configuration",
        });
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const url = new URL(`${origin}${normalizedPath}`);
    Object.entries(query || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        url.searchParams.set(key, String(value));
    });
    return url.toString();
}

function errorKindFromStatus(status: number): MystudioErrorKind {
    if (status === 401) return "authentication";
    if (status === 403) return "authorization";
    if (status === 404) return "not_found";
    if (status === 400 || status === 409 || status === 422) return "validation";
    if (status >= 500) return "server";
    return "unknown";
}

function errorMessage(kind: MystudioErrorKind): string {
    switch (kind) {
        case "authentication":
            return "로그인이 만료되었습니다. 다시 로그인해 주세요.";
        case "authorization":
            return "이 작업을 수행할 권한이 없습니다.";
        case "not_found":
            return "요청한 데이터를 찾을 수 없습니다.";
        case "validation":
            return "입력한 내용을 확인해 주세요.";
        case "server":
            return "서버 연결에 실패했습니다. 잠시 후 다시 시도해 주세요.";
        case "timeout":
            return "요청 시간이 초과되었습니다. 네트워크를 확인해 주세요.";
        case "offline":
            return "인터넷 연결을 확인해 주세요.";
        case "configuration":
            return "MyStudio API 연결 주소가 설정되지 않았습니다.";
        case "invalid_response":
            return "서버 응답 형식을 확인할 수 없습니다.";
        default:
            return "데이터를 불러오지 못했습니다.";
    }
}

function notifyAuthenticationRequired(status: number, requestId: string): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(MYSTUDIO_AUTH_REQUIRED_EVENT, {
        detail: { status, requestId },
    }));
}

/**
 * The v4 Nest guard consumes the authenticated customer session token.
 * Never source this value from a REACT_APP_* variable because Create React App
 * embeds those values in the public browser bundle.
 */
export function resolveMystudioBearerToken(): string {
    return String(getAccountData.token() || "").trim();
}

function getRequestId(response: Response, payload: unknown): string {
    const headerRequestId = response.headers.get("x-request-id")
        || response.headers.get("x-correlation-id")
        || "";
    if (headerRequestId) return headerRequestId;
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
        const root = payload as Record<string, unknown>;
        const meta = root.meta && typeof root.meta === "object"
            ? root.meta as Record<string, unknown>
            : null;
        return String(meta?.requestId || meta?.request_id || root.requestId || "");
    }
    return "";
}

function unwrapPayload<T>(payload: unknown, requestId: string): MystudioApiEnvelope<T> {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        throw new MystudioApiError({
            message: errorMessage("invalid_response"),
            kind: "invalid_response",
            requestId,
            details: payload,
        });
    }

    const root = payload as Record<string, unknown>;
    const data = Object.prototype.hasOwnProperty.call(root, "result")
        ? root.result
        : undefined;

    if (data === undefined) {
        throw new MystudioApiError({
            message: errorMessage("invalid_response"),
            kind: "invalid_response",
            requestId,
            details: payload,
        });
    }

    const rawMeta = root.meta && typeof root.meta === "object" && !Array.isArray(root.meta)
        ? root.meta as Record<string, unknown>
        : {};
    const meta: MystudioApiMeta = {
        requestId: String(rawMeta.requestId || rawMeta.request_id || requestId || ""),
        source: rawMeta.source === "nest" || rawMeta.source === "preview" ? rawMeta.source : "erp",
        nextCursor: rawMeta.nextCursor === null
            ? null
            : String(rawMeta.nextCursor || rawMeta.next_cursor || "") || undefined,
        total: Number.isFinite(Number(rawMeta.total)) ? Number(rawMeta.total) : undefined,
    };

    return { data: data as T, meta };
}

export async function requestMystudioV4<T>({
    path,
    method = "GET",
    query,
    body,
    signal,
    timeoutMs = DEFAULT_V4_TIMEOUT_MS,
    idempotencyKey,
    authenticated = true,
}: {
    path: string;
    method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
    query?: Record<string, unknown>;
    body?: unknown;
    signal?: AbortSignal;
    timeoutMs?: number;
    idempotencyKey?: string;
    authenticated?: boolean;
}): Promise<MystudioApiEnvelope<T>> {
    const token = authenticated ? resolveMystudioBearerToken() : "";
    if (authenticated && isPublicPasswordlessDemoSession({ token })) {
        throw new MystudioApiError({
            kind: "configuration",
            message: "체험 모드에서는 실제 고객 정보를 조회하거나 변경할 수 없습니다.",
        });
    }
    const url = buildUrl(path, query);
    const controller = new AbortController();
    const abortFromCaller = () => controller.abort();
    signal?.addEventListener("abort", abortFromCaller, { once: true });
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(url, {
            method,
            // v4 authentication is bearer-token based. Do not attach browser
            // cookies to the separate API origin or require credentialed CORS.
            credentials: "omit",
            cache: "no-store",
            signal: controller.signal,
            headers: {
                Accept: "application/json",
                ...(body === undefined ? {} : { "Content-Type": "application/json" }),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
            },
            body: body === undefined ? undefined : JSON.stringify(body),
        });

        let payload: unknown = null;
        if (response.status !== 204) {
            try {
                payload = await response.json();
            } catch {
                payload = null;
            }
        } else {
            payload = { result: null };
        }

        const requestId = getRequestId(response, payload);
        if (!response.ok) {
            const kind = errorKindFromStatus(response.status);
            if (kind === "authentication" && authenticated) {
                notifyAuthenticationRequired(response.status, requestId);
            }
            const root = payload && typeof payload === "object" && !Array.isArray(payload)
                ? payload as Record<string, unknown>
                : {};
            const serverMessage = String(
                root.message
                || (root.status && typeof root.status === "object"
                    ? (root.status as Record<string, unknown>).msg
                    : "")
                || (root.error && typeof root.error === "object"
                    ? (root.error as Record<string, unknown>).message
                    : "")
                || "",
            ).trim();
            throw new MystudioApiError({
                message: serverMessage || errorMessage(kind),
                kind,
                status: response.status,
                requestId,
                details: payload,
            });
        }

        return unwrapPayload<T>(payload, requestId);
    } catch (error) {
        if (error instanceof MystudioApiError) throw error;
        if (controller.signal.aborted) {
            if (signal?.aborted) throw error;
            throw new MystudioApiError({
                message: errorMessage("timeout"),
                kind: "timeout",
            });
        }
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            throw new MystudioApiError({
                message: errorMessage("offline"),
                kind: "offline",
                details: error,
            });
        }
        throw new MystudioApiError({
            message: errorMessage("server"),
            kind: "server",
            details: error,
        });
    } finally {
        window.clearTimeout(timeout);
        signal?.removeEventListener("abort", abortFromCaller);
    }
}

function createIdempotencyKey(scope: string): string {
    const random = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${scope}-${random}`;
}

export const mystudioV4 = {
    login: (identifier: string, password: string) =>
        requestMystudioV4<MystudioLoginResult>({
            path: "/api/v4/auth/login",
            method: "POST",
            body: { identifier, password },
            authenticated: false,
        }),
    publicContent: (query: Record<string, unknown> = {}, signal?: AbortSignal) =>
        requestMystudioV4<V4ContentListDto>({
            path: "/api/v4/public/content",
            query,
            signal,
        }),
    applyPublicEvent: (contentId: string, message?: string) =>
        requestMystudioV4<Record<string, unknown>>({
            path: `/api/v4/events/${encodeURIComponent(contentId)}/application`,
            method: "POST",
            body: message ? { message } : {},
            idempotencyKey: createIdempotencyKey("event-application"),
        }),
    home: (signal?: AbortSignal) =>
        requestMystudioV4<V4HomeDto>({ path: "/api/v4/me/home", signal }),
    galleryPosts: (query: Record<string, unknown> = {}, signal?: AbortSignal) =>
        requestMystudioV4<V4PageResultDto<V4GalleryPostDto>>({
            path: "/api/v4/me/gallery-posts",
            query: {
                page: query.page,
                limit: query.limit,
                categoryId: query.categoryId,
                sort: query.sort,
            },
            signal,
        }),
    gallerySections: (signal?: AbortSignal) =>
        requestMystudioV4<V4GallerySectionDto[]>({
            path: "/api/v4/me/gallery-sections",
            signal,
        }),
    galleryPost: (
        postId: string,
        query: Record<string, unknown> = {},
        signal?: AbortSignal,
    ) =>
        requestMystudioV4<V4GalleryPostDto>({
            path: `/api/v4/me/gallery-posts/${encodeURIComponent(postId)}`,
            query: { page: query.page, limit: query.limit },
            signal,
        }),
    registerGalleryPostView: (postId: string) =>
        requestMystudioV4<{ viewCount: number }>({
            path: `/api/v4/me/gallery-posts/${encodeURIComponent(postId)}/view`,
            method: "POST",
        }),
    schedules: (signal?: AbortSignal) =>
        requestMystudioV4<V4PageResultDto<V4ScheduleDto> | V4ScheduleDto[]>({
            path: "/api/v4/me/schedules",
            signal,
        }),
    themes: (query: Record<string, unknown> = {}, signal?: AbortSignal) =>
        requestMystudioV4<V4AvailabilityDto<V4ThemeDto> | V4PageResultDto<V4ThemeDto> | V4ThemeDto[]>({
            path: "/api/v4/me/themes",
            query: {
                page: query.page,
                limit: query.limit,
                scheduleId: query.scheduleId,
            },
            signal,
        }),
    products: (signal?: AbortSignal) =>
        requestMystudioV4<V4AvailabilityDto<V4ProductDto> | V4PageResultDto<V4ProductDto> | V4ProductDto[]>({
            path: "/api/v4/me/products",
            signal,
        }),
    photoDeliveries: (signal?: AbortSignal) =>
        requestMystudioV4<V4AvailabilityDto<V4PhotoDeliveryDto> | V4PhotoDeliveryDto[]>({
            path: "/api/v4/me/photo-deliveries",
            signal,
        }),
    photoDeliveryAssets: (
        deliveryId: string,
        query: Record<string, unknown> = {},
        signal?: AbortSignal,
    ) =>
        requestMystudioV4<V4PageResultDto<V4PhotoAssetDto>>({
            path: `/api/v4/me/photo-deliveries/${encodeURIComponent(deliveryId)}/assets`,
            query,
            signal,
        }),
    createPhotoDownloadTicket: (deliveryId: string) =>
        requestMystudioV4<{
            ticket: string;
            expiresAt: string;
            downloadUrl: string;
            availability: string;
        }>({
            path: `/api/v4/me/photo-deliveries/${encodeURIComponent(deliveryId)}/download-ticket`,
        }),
    myPage: (signal?: AbortSignal) =>
        requestMystudioV4<V4MeDto>({ path: "/api/v4/me", signal }),
    actionItems: (signal?: AbortSignal) =>
        requestMystudioV4<{ items?: V4ActionItemDto[] }>({
            path: "/api/v4/me/action-items",
            signal,
        }),
    terms: (signal?: AbortSignal) =>
        requestMystudioV4<V4AvailabilityDto<V4TermsDto>>({
            path: "/api/v4/me/terms",
            signal,
        }),
    saveDevice: (playerId: string) =>
        requestMystudioV4<{ saved: true }>({
            path: "/api/v4/me/device",
            method: "PUT",
            body: { playerId },
        }),
    changePassword: (currentPassword: string, newPassword: string) =>
        requestMystudioV4<{ changed: true }>({
            path: "/api/v4/me/password-change",
            method: "POST",
            body: { currentPassword, newPassword },
        }),
    markNotificationsRead: (notificationIds: number[]) =>
        requestMystudioV4<{ updatedCount: number }>({
            path: "/api/v4/me/notifications/read",
            method: "POST",
            body: { ids: notificationIds },
        }),
    inquiries: (query: Record<string, unknown> = {}, signal?: AbortSignal) =>
        requestMystudioV4<V4PageResultDto<V4InquiryDto> | V4InquiryDto[]>({
            path: "/api/v4/me/inquiries",
            query,
            signal,
        }),
    createInquiry: (input: CreateMystudioInquiryInput) =>
        requestMystudioV4<V4InquiryDto>({
            path: "/api/v4/me/inquiries",
            method: "POST",
            body: input,
            idempotencyKey: createIdempotencyKey("inquiry"),
        }),
    inquiry: (requestId: string, signal?: AbortSignal) =>
        requestMystudioV4<V4InquiryDto>({
            path: `/api/v4/me/inquiries/${encodeURIComponent(requestId)}`,
            signal,
        }),
    addInquiryMessage: (requestId: string, message: string) =>
        requestMystudioV4<V4InquiryDto>({
            path: `/api/v4/me/inquiries/${encodeURIComponent(requestId)}/messages`,
            method: "POST",
            body: { message },
            idempotencyKey: createIdempotencyKey(`inquiry-message-${requestId}`),
        }),
    markInquiryRead: (requestId: string) =>
        requestMystudioV4<{ requestId: number; updatedCount: number }>({
            path: `/api/v4/me/inquiries/${encodeURIComponent(requestId)}/read`,
            method: "POST",
        }),
    notifications: (query: Record<string, unknown> = {}, signal?: AbortSignal) =>
        requestMystudioV4<V4PageResultDto<V4NotificationDto> | V4NotificationDto[]>({
            path: "/api/v4/me/notifications",
            query,
            signal,
        }),
    notificationPreferences: (signal?: AbortSignal) =>
        requestMystudioV4<V4NotificationPreferencesDto>({
            path: "/api/v4/me/notification-preferences",
            signal,
        }),
    updateNotificationPreferences: (
        input: UpdateMystudioNotificationPreferencesInput,
    ) =>
        requestMystudioV4<V4NotificationPreferencesDto>({
            path: "/api/v4/me/notification-preferences",
            method: "PUT",
            body: input,
        }),
    markNotificationRead: (notificationId: string) =>
        requestMystudioV4<{ updatedCount: number }>({
            path: "/api/v4/me/notifications/read",
            method: "POST",
            body: { ids: [Number(notificationId)] },
        }),
    toggleThemeFavorite: (themeId: string, favorite: boolean) =>
        requestMystudioV4<Record<string, unknown>>({
            path: "/api/v4/me/theme-favorites",
            method: "PUT",
            body: { themeId: Number(themeId), active: favorite },
            idempotencyKey: createIdempotencyKey("theme-favorite"),
        }),
    submitThemeSelection: (scheduleId: string, input: SubmitThemeSelectionInput) =>
        requestMystudioV4<V4ScheduleDto>({
            path: `/api/v4/me/schedules/${encodeURIComponent(scheduleId)}/theme-selection`,
            method: "PUT",
            body: {
                themeIds: input.themeIds.map(Number),
            },
            idempotencyKey: createIdempotencyKey("theme-selection"),
        }),
    reviewEvents: (signal?: AbortSignal) =>
        requestMystudioV4<V4AvailabilityDto<V4ReviewEventDto>>({
            path: "/api/v4/review-events",
            signal,
        }),
    applyReviewEvent: (
        reviewEventId: string,
        input: ApplyMystudioReviewEventInput,
    ) =>
        requestMystudioV4<Record<string, unknown>>({
            path: `/api/v4/review-events/${encodeURIComponent(reviewEventId)}/application`,
            method: "POST",
            body: input,
            idempotencyKey: createIdempotencyKey("review-event"),
        }),
    createProductDraft: (input: CreateProductDraftInput) =>
        requestMystudioV4<Record<string, unknown>>({
            path: `/api/v4/me/products/${encodeURIComponent(input.contractProductId)}/draft`,
            method: "POST",
            body: {
                requestedPhotoCount: input.requestedPhotoCount,
                metadata: input.metadata,
            },
            idempotencyKey: createIdempotencyKey("product-draft"),
        }),
    replaceProductPhotoSelection: (requestId: string, assetIds: number[]) =>
        requestMystudioV4<{ requestId: number; selectedPhotoCount: number; status: string }>({
            path: `/api/v4/me/product-requests/${encodeURIComponent(requestId)}/photo-selection`,
            method: "PUT",
            body: {
                items: assetIds.map((assetId, index) => ({
                    assetId,
                    sortOrder: index + 1,
                })),
            },
        }),
    submitProductRequest: (requestId: string) =>
        requestMystudioV4<{ requestId: number; status: string }>({
            path: `/api/v4/me/product-requests/${encodeURIComponent(requestId)}/submit`,
            method: "POST",
            body: {},
            idempotencyKey: createIdempotencyKey("product-submit"),
        }),
    updateShipping: (input: UpdateMystudioShippingInput) =>
        requestMystudioV4<Record<string, unknown>>({
            path: "/api/v4/me/shipping-address",
            method: "POST",
            body: input,
            idempotencyKey: createIdempotencyKey("shipping"),
        }),
};

export function shouldRetryMystudioV4(failureCount: number, error: unknown): boolean {
    if (failureCount >= 1) return false;
    if (!(error instanceof MystudioApiError)) return false;
    return error.kind === "server" || error.kind === "timeout" || error.kind === "offline";
}

export function getMystudioErrorMessage(error: unknown): string {
    return error instanceof MystudioApiError
        ? error.message
        : "데이터를 불러오지 못했습니다.";
}
